import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding,
  assignPathDirectly
} from '../actions/onboardingActions';
import { loadUser } from '../actions/authActions';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { Zap, BookOpen, Trophy, Sparkles } from 'lucide-react';

const easing = [0.22, 1, 0.36, 1];

const OnboardingPage = ({
  onboarding,
  auth,
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding,
  assignPathDirectly,
  loadUser
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false); // Add this flag
  const [windowDimension, setWindowDimension] = useState({ width: 300, height: 300 });
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Set window dimensions for confetti
    setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    // Don't run useEffect if we're navigating or assessment is completed
    if (isNavigating || assessmentCompleted) {
      return;
    }

    if (!auth.isAuthenticated) {
      navigate('/');
      return;
    }

    // Only students should access onboarding
    if (auth.user && auth.user.role !== 'student') {
      navigate('/dashboard');
      return;
    }

    // Referral users must see the claim page before onboarding
    if (sessionStorage.getItem('referral_registration') && !sessionStorage.getItem('skillsbuild_claimed') && !sessionStorage.getItem('skipped_claim')) {
      navigate('/claim-skillsbuild');
      return;
    }

    // Don't load questions if we already have results or are submitting
    if (onboarding.result || showResult || isSubmitting) {
      return;
    }

    // Skip assessment if user has completed onboarding OR already has a path assigned (e.g. from referral with pathwayId)
    if (auth.user && (auth.user.hasCompletedOnboarding || auth.user.path) && !assessmentCompleted && !isNavigating) {
      setIsNavigating(true);
      navigate('/dashboard');
      return;
    }

    // If referral link included a pathwayId, assign path directly and skip assessment
    const referralPathwayId = sessionStorage.getItem('referral_pathwayId');
    if (referralPathwayId) {
      sessionStorage.removeItem('referral_pathwayId');
      setIsNavigating(true);
      assignPathDirectly(referralPathwayId)
        .then(() => navigate('/dashboard'))
        .catch(() => {
          setIsNavigating(false);
          getOnboardingQuestions();
        });
      return;
    }

    getOnboardingQuestions();

    return () => {
      clearOnboarding();
    };
  }, [auth.isAuthenticated, auth.user, onboarding.result, showResult, isSubmitting, isNavigating, assessmentCompleted, getOnboardingQuestions, clearOnboarding, navigate]);

  useEffect(() => {
    if (onboarding.result) {
      // Immediate User State Update
      if (onboarding.result.user) {
        dispatch({ type: 'USER_LOADED', payload: onboarding.result.user });
      } else {
        loadUser();
      }

      // Conditional Result Display with Fallback Mechanism
      const checkAndShowResult = () => {
        if (auth.user && auth.user.hasCompletedOnboarding) {
          setShowResult(true);
          setIsSubmitting(false);
          setAssessmentCompleted(true);
          setShowConfetti(true);

          // Stop confetti after 4 seconds using requestAnimationFrame
          const startTime = performance.now();
          const stopConfetti = () => {
            const elapsed = performance.now() - startTime;
            if (elapsed < 4000) {
              requestAnimationFrame(stopConfetti);
            } else {
              setShowConfetti(false);
            }
          };
          requestAnimationFrame(stopConfetti);
        } else {
          // Fallback: wait 500ms and check again
          setTimeout(checkAndShowResult, 500);
        }
      };

      checkAndShowResult();
    }
  }, [onboarding.result, auth.user, loadUser, dispatch]);

  // Cleanup effect to reset navigation state
  useEffect(() => {
    return () => {
      setIsNavigating(false);
    };
  }, []);

  const handleAnswerSelect = (optionIndex) => {
    const currentQuestion = onboarding.questions[currentQuestionIndex];
    
    // Update answers array
    const newAnswers = [...answers];
    const existingAnswerIndex = newAnswers.findIndex(
      answer => answer.questionId === currentQuestion._id
    );

    if (existingAnswerIndex !== -1) {
      newAnswers[existingAnswerIndex] = {
        questionId: currentQuestion._id,
        optionIndex
      };
    } else {
      newAnswers.push({
        questionId: currentQuestion._id,
        optionIndex
      });
    }

    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < onboarding.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (answers.length === onboarding.questions.length) {
      setIsSubmitting(true);
      submitOnboardingAnswers(answers);
    }
  };

  // Check if user has placeholder profile (self-registered via external link)
  const needsProfileCompletion = auth.user && (
    auth.user.name === 'New' || auth.user.country === 'Not specified'
  );

  const handleContinue = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      // First clear onboarding state to prevent loops
      clearOnboarding();

      // Refresh user data to ensure hasCompletedOnboarding is updated
      await loadUser();

      // Wait a bit more for the data to propagate in slow environments
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Always go to dashboard after assessment
      navigate('/dashboard');
    } catch (error) {
      console.error('Error refreshing user data:', error);
      navigate('/dashboard');
    }
  };

  // Show loading state for initial load only, not for submission
  if (onboarding.loading && !isSubmitting && !showResult) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8">
          <div className="text-[#1e2d40] text-lg sm:text-xl text-center">Loading assessment...</div>
        </div>
      </div>
    );
  }

  // Show submission loading state
  if (isSubmitting && onboarding.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center max-w-md mx-auto">
          <div className="text-[#1e2d40] text-lg sm:text-xl mb-4">Submitting your assessment...</div>
          <div className="text-gray-600 text-sm">Please wait while we process your answers</div>
        </div>
      </div>
    );
  }

  // Show error if there are any API errors (but not during submission)
  if (!onboarding.loading && !onboarding.questions.length && !showResult && !isSubmitting) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="max-w-md mx-auto bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center">
          <div className="mb-6">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-2">Assessment Unavailable</h2>
            <p className="text-[#1e2d40] mb-4 text-sm sm:text-base">
              Unable to load your assessment questions. This could be because:
            </p>
            <ul className="text-left text-gray-600 text-sm space-y-1 mb-6">
              <li>• You are not assigned to a group</li>
              <li>• Your group doesn't have a program</li>
              <li>• No questions are available for your program</li>
            </ul>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#1086a4] hover:bg-[#054581] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (showResult && onboarding.result) {
    const firstName = auth.user?.name?.split(' ')[0] || 'Student';
    const resultProgramTitle = onboarding.result.programTitle || 'the program';
    const resultPathTitle = onboarding.result.pathTitle || 'Your Learning Path';
    const resultPathDescription = onboarding.result.assignedPath?.description || null;
    const resultCoursesCount = onboarding.result.assignedPath?.courses?.length || 0;

    return (
      <div className="min-h-screen bg-background overflow-hidden">
        {showConfetti && (
          <Confetti
            width={windowDimension.width}
            height={windowDimension.height}
            gravity={0.25}
            numberOfPieces={250}
            recycle={false}
            colors={['hsl(var(--primary))', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4']}
          />
        )}

        <div className="max-w-6xl mx-auto">
          {/* Top bar */}
          <div className="border-b border-border bg-background px-5 sm:px-10 py-3.5 flex items-center justify-between">
            <div className="leading-tight min-w-0">
              <p className="font-display font-bold text-foreground tracking-tight truncate text-[15px] sm:text-base">
                {resultProgramTitle}
              </p>
              {auth.user?.group?.title && (
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">
                  {auth.user.group.title}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="flex items-center gap-1.5 text-sm font-bold text-foreground tabular-nums">
                <Zap className="w-4 h-4 text-xp" />
                0 XP
              </span>
            </div>
          </div>

          {/* Hero section */}
          <div className="bg-background px-5 sm:px-10 pt-10 sm:pt-16 pb-7 sm:pb-9">
            <motion.div
              className="flex items-center gap-2.5 mb-4"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.05, ease: easing }}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
              <span className="text-[10px] font-bold text-primary uppercase tracking-[0.22em]">
                Assessment Complete
              </span>
            </motion.div>

            <motion.h1
              className="font-display font-bold text-foreground tracking-tight leading-[1.05]"
              style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', letterSpacing: '-0.025em' }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12, ease: easing }}
            >
              Welcome aboard.
            </motion.h1>

            <motion.p
              className="mt-2 text-[14px] sm:text-[15px] font-semibold uppercase tracking-[0.16em] text-foreground/55"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22, ease: easing }}
            >
              Your journey starts now
            </motion.p>
          </div>

          <div className="h-px bg-border mx-5 sm:mx-10" />

          {/* Path assignment card */}
          <div className="px-5 sm:px-10 py-8 sm:py-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: easing }}
            >
              <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
                Your Assigned Path
              </p>

              <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display font-bold text-foreground text-xl sm:text-2xl tracking-tight mb-1">
                      {resultPathTitle}
                    </h2>
                    {resultPathDescription && (
                      <p className="text-foreground/60 text-sm sm:text-[15px] leading-relaxed mt-2">
                        {resultPathDescription}
                      </p>
                    )}
                    {resultCoursesCount > 0 && (
                      <div className="flex items-center gap-3 mt-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                          <BookOpen className="w-3.5 h-3.5" />
                          {resultCoursesCount} {resultCoursesCount === 1 ? 'Course' : 'Courses'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="h-px bg-border mx-5 sm:mx-10" />

          {/* Welcome video */}
          <motion.div
            className="px-5 sm:px-10 py-8 sm:py-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: easing }}
          >
            <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
              Welcome Video
            </p>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/Frb72eDy5fs"
                  title="Welcome Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>

          <div className="h-px bg-border mx-5 sm:mx-10" />

          {/* What's Next */}
          <motion.div
            className="px-5 sm:px-10 py-8 sm:py-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5, ease: easing }}
          >
            <p className="text-[10px] font-bold text-primary uppercase tracking-[0.22em] mb-5">
              What's Next
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-3">
                  <FaCheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <p className="font-bold text-foreground text-sm mb-1">Assessment Done</p>
                <p className="text-foreground/50 text-xs leading-relaxed">
                  You've completed your initial assessment successfully.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-5">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <p className="font-bold text-foreground text-sm mb-1">Path Assigned</p>
                <p className="text-foreground/50 text-xs leading-relaxed">
                  Your personalized learning path is ready for you.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-5">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                  <Trophy className="w-5 h-5 text-amber-500" />
                </div>
                <p className="font-bold text-foreground text-sm mb-1">Start Earning XP</p>
                <p className="text-foreground/50 text-xs leading-relaxed">
                  Complete lessons and courses to earn XP and badges.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Continue button */}
          <motion.div
            className="px-5 sm:px-10 pb-10 sm:pb-14"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6, ease: easing }}
          >
            <button
              onClick={handleContinue}
              disabled={isNavigating}
              className={`w-full sm:w-auto flex items-center justify-center gap-2.5 font-bold py-3.5 px-8 rounded-xl transition-colors duration-200 text-sm tracking-wide ${
                isNavigating
                  ? 'bg-foreground/20 text-foreground/40 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}
            >
              {isNavigating ? 'Updating your profile...' : needsProfileCompletion ? 'Complete Your Profile' : 'Continue to Dashboard'}
              {!isNavigating && <FaArrowRight className="w-3.5 h-3.5" />}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Don't show questions if we have results or are submitting
  if (showResult || isSubmitting) {
    return null; // This will be handled by the result display logic above
  }

  if (!onboarding.questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20 pb-20 lg:pb-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 p-6 sm:p-8 text-center max-w-md mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-[#1e2d40] mb-4">No Assessment Available</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">There are no questions available for your program.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#1086a4] hover:bg-[#054581] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = onboarding.questions[currentQuestionIndex];
  const currentAnswer = answers.find(answer => answer.questionId === currentQuestion._id);
  const progress = ((currentQuestionIndex + 1) / onboarding.questions.length) * 100;
  const isLastQuestion = currentQuestionIndex === onboarding.questions.length - 1;
  const canProceed = currentAnswer !== undefined;
  const allQuestionsAnswered = answers.length === onboarding.questions.length;

  return (
    <div className="min-h-screen overflow-auto pt-16 pb-16 lg:pb-0">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 min-h-screen">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-[#054581]">Find your path</h1>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            Program: <span className="text-[#1086a4] font-semibold">{onboarding.programTitle}</span>
          </p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <motion.div
              className="bg-gradient-to-r from-[#1086a4] to-[#054581] h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {onboarding.questions.length}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="bg-card backdrop-blur-sm rounded-xl shadow-xl border border-border p-6 sm:p-8 mb-6 sm:mb-8"
          >
            <h2 className="text-lg sm:text-xl font-semibold mb-6 text-foreground">{currentQuestion.text}</h2>

            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors duration-200 ${
                    currentAnswer?.optionIndex === index
                      ? 'border-[#1086a4] bg-[#1086a4]/10 text-[#1086a4]'
                      : 'border-border bg-card hover:border-[#1086a4] hover:bg-muted text-foreground'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="w-6 h-6 rounded-full border-2 border-current mr-4 flex items-center justify-center flex-shrink-0">
                      {currentAnswer?.optionIndex === index && (
                        <span className="w-3 h-3 rounded-full bg-current"></span>
                      )}
                    </span>
                    <span className="text-sm sm:text-base">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
              currentQuestionIndex === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            Previous
          </button>

          <div className="text-center order-first sm:order-none">
            <p className="text-xs sm:text-sm text-gray-600">
              {answers.length} of {onboarding.questions.length} questions answered
            </p>
          </div>

          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                allQuestionsAnswered
                  ? 'bg-[#1086a4] text-white hover:bg-[#054581]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Submit Assessment
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                canProceed
                  ? 'bg-[#1086a4] text-white hover:bg-[#054581]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  auth: state.auth
});

export default connect(mapStateToProps, {
  getOnboardingQuestions,
  submitOnboardingAnswers,
  clearOnboarding,
  assignPathDirectly,
  loadUser
})(OnboardingPage);
